import {
  AfterViewInit,
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { firstValueFrom, Subscription } from 'rxjs';
import { L360Service } from '@workflow/ecp-workflow/dynamic-app/service/l360/l360.service';

import { EaGridColumn, EaGridOptions } from '@cudirect/shared-ui-library/plugins';
import { BundleHelperService } from '@shared/service/bundle-helper.service';
import { InputStatus } from '@workflow/directive/input-mask/input-mask-processing.base';
import { maskInput } from '@workflow/directive/input-mask/mask.helper';
import { ApplicationApiService } from '@workflow/ecp-workflow/dynamic-app/service/application-api/application-api.service';
import {
  Product,
  ApplicantProduct,
  APMembershipOptions,
  ShareCertificateTerms,
  ProductTerm,
  OptionType,
  IOaoDropdown
} from '@workflow/ecp-workflow/dynamic-app/types/accounts-and-services.type';
import { getValidationObject } from '@workflow/helper/validation-converter/validation-converter';
import { StatefulService } from '@app/service/stateful.service';
import { FieldInstance } from '@workflow/class/field.service';

/** Share Dividend Type */
const SHARE_DIVIDEND_TYPE = 'ShareDividendType';
/** Share Disbursement Type */
const SHARE_DISBURSEMENT_TYPE = 'ShareDisbursementType';
/** Share Contribution Source */
const SHARE_CONTRIBUTION_SOURCE = 'ShareContributionSource';
/** Shares & Services Picklist Path */
const SHARE_SERVICES_PICKLIST_PATH = 'ss:oao-accounts-and-services-picklist';

/** OAO checking and saving accounts component */
@Component({
  selector: 'ecp-oao-share-certificates-account',
  styleUrls: ['./oao-share-certificates-account.component.scss'],
  templateUrl: './oao-share-certificates-account.component.html',
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class OaoShareCertificatesAccountComponent implements OnInit, AfterViewInit {
  private readonly statefulService = inject(StatefulService);

  /** oao account objcet */
  @Input() public mainAccount: Product[];
  /** all account products */
  @Input() public allProducts: Product[];
  /** co-applicant shares */
  @Input() public coApplicantProducts: ApplicantProduct[];
  /** field */
  @Input() public field: FieldInstance;
  /** Callback Parent method to enable the continue button if all validations are valid */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Output() public enableContinueButton = new EventEmitter<any>();
  /** Is Initial Loading from L360 Call */
  @Input() public isInitialLoading: boolean;
  /** share certificate options */
  @Input() public shareOptions: APMembershipOptions;

  /** DEV NOTE: Angular 8 -
   * either need to set static to true to keep 'sharedCertForm' in onInit
   * or need to move 'sharedCertForm' reference to ngAfterViewInit
   */
  @ViewChild('sharedCertForm', { static: true }) public sharedCertForm: NgForm;

  @ViewChild('selectionTemplate', { static: true }) public selectionTemplate: TemplateRef<HTMLElement>;
  @ViewChild('term', { static: true }) public term: TemplateRef<HTMLElement>;
  @ViewChild('gridForm', { static: true }) public gridForm: TemplateRef<HTMLElement>;
  @ViewChild('minBalance', { static: true }) public minBalance: TemplateRef<HTMLElement>;

  public gridColumns: EaGridColumn[] = [];
  public gridOptions: EaGridOptions = {};

  // Ea Grid options
  public gridData: [] = [];

  /** Co Applicants Services : Local Variable */
  public coAppProducts: ApplicantProduct[];
  /** Valid Form or Not */
  public isValidForm: boolean;
  /** form subscription */
  public frmSub: Subscription;
  /** focused flag */
  public focused = false;
  /** last state of mobile number input */
  private lastState: InputStatus;
  /** Storage ID */
  public arrayStorageId = 'api:oaoMemberInformation/accountsAndServices/primaryApplicant/shares';
  /** current year */
  public currentYear = new Date().getFullYear();
  /** Share terms */
  public shareTerms: ShareCertificateTerms[] = [];
  /** Share Dividend Type Picklist */
  public shareDividendTypePicklist: IOaoDropdown[];
  /** Share Dividend Type Picklist */
  public shareDisbursementTypePicklist: IOaoDropdown[];
  /** Share Contribution Source Picklist */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public shareContributionSourceList: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public shareCertificate: any[];
  constructor(
    public readonly applicationApiService: ApplicationApiService,
    public readonly l360Service: L360Service,
    private readonly bundleHelperService: BundleHelperService
  ) {}

  public ngOnInit(): void {
    this.coAppProducts = this.coApplicantProducts ? this.coApplicantProducts.map((x) => Object.assign({}, x)) : [];
    this.ShareCertificate();
    /** Subscribe ngForm status change event */
    this.frmSub = this.sharedCertForm.statusChanges.subscribe((x) => {
      this.isValidForm = x.toString().toLowerCase() === 'valid' ? true : false;
      // Check User selected the shares & its term correct, if so then check the form validation
      const pageType =
        (this.mainAccount && this.mainAccount[0].type.toLowerCase()) === 'sharecertificates' ? 'shared' : 'retirement-shared';
      if (this.CheckValidPageSelection()) {
        this.emitValidity(pageType, this.isValidForm);
      } else {
        this.emitValidity(pageType, false);
      }
    });

    // Page Validation check on page load
    this.ValidatePage();
    this.initGridColumns();
    this.initGridOptions();
  }
  public ShareCertificate(): void {
    const shareGroupArr = new Set(this.mainAccount.map((item) => item.shareGroupID));
    this.shareCertificate = [];
    shareGroupArr.forEach((g) => {
      const filterShares = this.filterAndSortShares(this.mainAccount, g);
      this.shareCertificate.push({
        id: g,
        shareGroupName: filterShares[0]?.shareGroupName,
        description: filterShares[0]?.description,
        isSelected: filterShares.filter((x) => x.terms.some((z) => z.isSelected === true) || x.isSelected).length > 0,
        optOut: filterShares[0]?.isSelected && !(filterShares.filter((x) => x.terms.some((z) => z.isSelected === true)).length > 0),
        term: filterShares.map((s, i) => {
          return {
            ...s,
            rate: s.terms[0]?.current,
            apy: s.terms[0]?.currentAPY,
            minBalance: s.terms[0]?.minBalance,
            termFrom: s.terms[0]?.termPeriodFrom,
            expanded: s.terms.some((z) => z.isSelected === true),
            rowIndex: i,
            coApplicants: this.coAppProducts.map((app) => {
              return {
                ...app,
                shares: this.filterAndSortShares(app.shares, g)
              };
            })
          };
        }),
        bundleIndex: filterShares[0]?.bundleIndex
      });
    });
  }

  public shareGroupElementID(sg, index, isAutomationId = false) {
    const sgName = sg.shareGroupName.toString().replace(/\s+/g, '').toLowerCase();
    return isAutomationId ? `${sgName}-accounts` : `${sg.id}-${sgName}-accounts-${index}`;
  }

  private filterAndSortShares(shares: Product[], groupId: number): Product[] {
    const filterShares = shares.filter((x) => x.shareGroupID === groupId);
    filterShares.forEach(
      (f) => (f.terms[0].sortNumber = f.terms[0]?.termPeriodTo > 0 ? f.terms[0]?.termPeriodFrom - 0.1 : f.terms[0]?.termPeriodFrom)
    );
    filterShares.sort((a, b) => a.terms[0]?.sortNumber - b.terms[0]?.sortNumber);
    return filterShares;
  }
  /**
   * Initiate the variables inside After ViewInit since we will get the input parameter only here
   */
  public ngAfterViewInit(): void {
    this.LoadPicklistData();
    this.mainAccount.forEach((item) => {
      if (item.terms && item.terms.length > 0) {
        const shareterm: ShareCertificateTerms = {
          id: item.id,
          termOptions: this.getTermDropDownValues(item.terms[0])
        };
        this.shareTerms.push(shareterm);
      }
    });
  }
  public isTermInMonthsValid(term: ProductTerm): boolean {
    return !term.isSelected || term.termInMonths !== '';
  }
  public isDividentOptionValid(term: ProductTerm): boolean {
    return !this.shareOptions?.dividendOptions?.isRequired || !term.isSelected || term.dividendOption !== '';
  }
  public isMaturityOption(term: ProductTerm): boolean {
    return !this.shareOptions?.maturityOptions?.isRequired || !term.isSelected || term.maturityOption !== '';
  }
  public isContributionSourceValid(term: ProductTerm): boolean {
    return !term.isSelected || term.contributionSource !== '';
  }
  public isContributionAmount(term: ProductTerm): boolean {
    return !term.isSelected || term.contributionAmount !== '';
  }
  /**
   * Determines if the dividend option should be visible for the given term.
   * It sets the default dividend option if the term is selected and no dividend option is set.
   * The visibility is determined based on the `isVisible` property of `dividendOptions` in `shareOptions`.
   * @param term - The ProductTerm object to set default dividendOption.
   * @returns A boolean indicating whether the dividend option is visible.
   */
  public isDividendOptionVisible(term: ProductTerm): boolean {
    if (!this.shareOptions || !this.shareOptions.dividendOptions) {
      return true;
    }
    if (term.isSelected && (!term.dividendOption || term.dividendOption === '')) {
      term.dividendOption = this.shareOptions.dividendOptions.defaultValue;
    }
    return this.shareOptions.dividendOptions.isVisible;
  }
  /**
   * Determines if the maturity option should be visible for the given term.
   * It sets the default maturity option if the term is selected and no maturity option is set.
   * The visibility is determined based on the `isVisible` property of `maturityOptions` in `shareOptions`.
   * @param term - The ProductTerm object to set default maturityOption.
   * @returns A boolean indicating whether the maturity option is visible.
   */
  public isMaturityOptionVisible(term: ProductTerm): boolean {
    if (!this.shareOptions || !this.shareOptions.maturityOptions) {
      return true;
    }
    if (term.isSelected && (!term.maturityOption || term.maturityOption === '')) {
      term.maturityOption = this.shareOptions.maturityOptions.defaultValue;
    }
    return this.shareOptions.maturityOptions.isVisible;
  }
  /**
   * Determines if the dividend option should be required for the given term.
   * Sets value based on `isRequired` property of `dividendOptions` in `shareOptions`.
   * @returns A boolean indicating whether the dividend option is required.
   */
  public isDividendOptionRequired(): boolean {
    if (!this.shareOptions || !this.shareOptions.dividendOptions) {
      return true;
    }
    return this.shareOptions.dividendOptions.isVisible && this.shareOptions.dividendOptions.isRequired;
  }
  /**
   * Determines if the dividend option should be required for the given term.
   * Sets value based on `isRequired` property of `maturityOptions` in `shareOptions`.
   * @returns A boolean indicating whether the maturity option is required.
   */
  public isMaturityOptionRequired(): boolean {
    if (!this.shareOptions || !this.shareOptions.maturityOptions) {
      return true;
    }
    return this.shareOptions.maturityOptions.isVisible && this.shareOptions.maturityOptions.isRequired;
  }
  public LoadPicklistData(): void {
    const picklist = this.statefulService.wrap(SHARE_SERVICES_PICKLIST_PATH).valueString;
    if (picklist) {
      const picklistTypes = JSON.parse(picklist);

      // Check Share Dividend Type
      const shareDividend = picklistTypes.filter((t) => t.value.toLowerCase() === SHARE_DIVIDEND_TYPE.toLowerCase());
      if (shareDividend && shareDividend.length === 1) {
        this.shareDividendTypePicklist = shareDividend[0].pickListItems;
      }

      // Check Share Disbursement Type
      const shareDisbursement = picklistTypes.filter((t) => t.value.toLowerCase() === SHARE_DISBURSEMENT_TYPE.toLowerCase());
      if (shareDisbursement && shareDisbursement.length === 1) {
        this.shareDisbursementTypePicklist = shareDisbursement[0].pickListItems;
      }

      // Check Share Contribution Source
      const shareContribution = picklistTypes.filter((t) => t.value.toLowerCase() === SHARE_CONTRIBUTION_SOURCE.toLowerCase());
      if (shareContribution && shareContribution.length === 1) {
        this.shareContributionSourceList = shareContribution[0].pickListItems;
      }
    }

    // Dividend options from admin settings
    if (this.shareOptions?.dividendOptions?.pickList) {
      this.shareDividendTypePicklist = this.shareOptions.dividendOptions.pickList;
    }

    // Maturity options from admin settings
    if (this.shareOptions?.maturityOptions?.pickList) {
      this.shareDisbursementTypePicklist = this.shareOptions.maturityOptions.pickList;
    }

    /** Get active picklist options */
    const activeDividendOptions = this.shareOptions?.dividendOptions?.pickList?.filter((t) => t.isActive);
    const activeMaturityOptions = this.shareOptions?.maturityOptions?.pickList?.filter((t) => t.isActive);
    if (activeDividendOptions && activeDividendOptions.length > 0) {
      this.shareDividendTypePicklist = activeDividendOptions;
    }
    if (activeMaturityOptions && activeMaturityOptions.length > 0) {
      this.shareDisbursementTypePicklist = activeMaturityOptions;
    }
  }

  /** get acocunt title */
  public get title(): string {
    return this.mainAccount.length > 0 ? this.mainAccount[0].typeName : '';
  }

  /** get account description */
  public get description(): string {
    return this.mainAccount.length > 0 ? this.mainAccount[0].typeDescription : '';
  }

  /** get/find Is this the checking account? */
  public get isShareCertificateAccount(): boolean {
    return this.mainAccount.length > 0 && this.mainAccount[0].type.toLowerCase() === 'sharecertificates';
  }

  /** Get Term Dropdown Values. */
  public getTermDropDownValues(term: ProductTerm): OptionType[] {
    const from = term.termPeriodFrom;
    const to = term.termPeriodTo;
    const returnVal: OptionType[] = [];

    // If period from & to provided then generate the terms inbetween from & to
    if (from && to) {
      for (let i = from; i <= to; i++) {
        returnVal.push({
          label: i.toString(),
          value: i.toString()
        });
      }
    }

    // If only either from/to period provided then, only return the one provided month
    if (from == null || to == null) {
      term.termInMonths = '';
      const value = from != null ? from : to;
      term.termInMonths = value.toString();
      term.hideTermInMonth = true;
      returnVal.push({
        label: value.toString(),
        value: value.toString()
      });
    }

    return returnVal;
  }

  public getTermOptions = (id: number): OptionType[] => {
    if (this.shareTerms && this.shareTerms.length > 0) {
      const terms = this.shareTerms.filter((f) => f.id === id);
      return terms && terms.length > 0 ? terms[0].termOptions : [];
    }
    return [];
  };

  /** When user uncheck the account selection, clear all other services selection inside the respective parent service */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public termSelectionChange(account, term: any): void {
    account.expanded = term.isSelected;
    account.isSelected = term.isSelected;
    if (!term.isSelected) {
      term.isSelected = false;
      term.shareNickName = '';
      term.termInMonths = '';
      term.dividendOption = '';
      term.maturityOption = '';
      term.taxYear = '';
      term.contributionSource = '';
      term.contributionAmount = '';
    } else {
      this.shareCertificate.forEach((sc) => {
        sc.isSelected = sc.term.some((x) => x.terms.some((z) => z.isSelected === true) || x.isSelected);
      });
      if (this.isYouthMembership()) {
        // If youth membership, then whatever share selected by Primary Applicant applicable for all co applicant's
        account.coApplicants.forEach((applicant) => {
          applicant.shares
            .filter((f) => f.id === account.id)
            .forEach((share) => {
              share.isSelected = true;
              share.isDisabled = true;
              share.terms.forEach((t) => (t.isSelected = true));
            });
        });
      }
      this.getTermDropDownValues(term);
    }
    this.UpdateBundleIndex();
    this.updateSelection();
  }

  /** Saves the user selection to session storage */
  public updateSelection(): void {
    this.statefulService.wrap('ss:oao-accounts-and-services').value = JSON.stringify(this.allProducts);
  }

  // This method will check whether user selected Any Share certificate (Top level)
  // if so check within the selected share, user selected any term?
  // Scenario validations
  // If user not selected any Share certificates , then no validation required hence returning valid
  // If user selected any share certificate then user has to select atleast one term within it. If not, returning not valid */
  public checkValidTermSelection(): boolean {
    /** Get All Selected share certificate account */
    const selectedAccount = this.shareCertificate.filter((f) => f.isSelected);
    const shareSelectionCount = selectedAccount.length;

    /** Check whether user selected terms within selected share certificates. */
    let i = 0;
    if (selectedAccount.length > 0) {
      selectedAccount.forEach((element) => {
        if (element.term.filter((f) => f.terms.some((x) => x.isSelected)).length > 0) {
          i = i + 1;
        }
      });
    }
    return shareSelectionCount === i;
  }

  /** Check the Retirement Account Mandatory fields Validation Check */
  public checkValidSelection(): boolean {
    /** Get All Selected retirement savings account */
    const selectedAccount = this.shareCertificate.filter((f) => f.isSelected);
    let isValid = true;
    if (selectedAccount.length > 0) {
      selectedAccount.forEach((element) => {
        element.term.forEach((x) => {
          x.terms
            .filter((f) => f.isSelected)
            .forEach((item) => {
              if (x.type.toLowerCase() === 'sharecertificates') {
                const valid =
                  item.termInMonths !== '' &&
                  (!this.isDividendOptionRequired() || item.dividendOption !== '') &&
                  (!this.isMaturityOptionRequired() || item.maturityOption !== '') &&
                  item.contributionAmount !== '';
                if (!valid) {
                  isValid = false;
                  x.inValid = true;
                  return;
                }
              } else {
                const valid =
                  item.termInMonths !== '' &&
                  (!this.isDividendOptionRequired() || item.dividendOption !== '') &&
                  (!this.isMaturityOptionRequired() || item.maturityOption !== '') &&
                  item.contributionSource !== '' &&
                  item.contributionAmount !== '';
                if (!valid) {
                  isValid = false;
                  x.inValid = true;
                  return;
                }
              }
            });
        });
      });
    }
    return isValid;
  }

  public CheckValidPageSelection(): boolean {
    return this.checkValidTermSelection() && this.checkValidSelection();
  }

  /**
   * Get all invalid field ids & expand accordion div if any invalid field exists
   * @returns Invalid field ids
   */
  public getInvalidFieldIds(): unknown[] {
    this.ValidatePage();
    const invalidList = [];
    const controls = this.sharedCertForm.controls;
    for (const name in controls) {
      if (controls[name].invalid) {
        invalidList.push(name);
      }
    }

    // Check term options valid or not??
    let divId = '';
    if (!(invalidList && invalidList.length > 0)) {
      this.shareCertificate.forEach((certificate, index: number) => {
        const showInvalidTerm = this.isErrorMessageAltDisplay(certificate, certificate.term);
        certificate.term.forEach((t) => {
          if (!t.expanded && t.isSelected && t.terms?.some((x) => x.isSelected) && t.inValid) {
            t.expanded = true;
            divId = `${this.shareGroupElementID(certificate, index)}`;
          } else if (t.expanded && t.terms?.every((x) => !x.isSelected)) t.expanded = false;
        });
        if (showInvalidTerm) {
          divId = `${this.shareGroupElementID(certificate, index)}`;
          return;
        }
      });
    }

    if (divId) invalidList.push(divId);

    return invalidList;
  }

  public ValidatePage(): void {
    const isValid = this.CheckValidPageSelection();
    const pageType = (this.mainAccount && this.mainAccount[0].type.toLowerCase()) === 'sharecertificates' ? 'shared' : 'retirement-shared';
    if (isValid) {
      this.emitValidity(pageType, true);
    } else {
      this.emitValidity(pageType, false);
    }
  }

  private emitValidity(pageType: string, valid) {
    const isOptOut = this.mainAccount.some((f) => f.requiredOptInType.toLowerCase() === 'opt-out');
    this.enableContinueButton.next({ page: pageType, valid, isOptOut });
  }

  /** methods helps to process mask */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public processMask(inputElement: any, term: ProductTerm, setSelection = true): void {
    const mask = maskInput('currency-decimals', this.readState(inputElement), this.lastState, getValidationObject(this.field));
    if (mask.changed) {
      inputElement.target.value = mask.processed.value;
      if (setSelection && inputElement.target.value) {
        inputElement.target.setSelectionRange(mask.processed.selection[0], mask.processed.selection[1]);
      }
      term.contributionAmount = mask.processed.value;
    }
    this.lastState = this.readState(inputElement);

    // Call update logic after masking, only on blur
    if (!setSelection) {
      this.updateTermDetailsIntoBundle();
    }
  }

  /** Read inputelement state */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private readState(inputElement: any): InputStatus {
    const domElement = inputElement.target;
    return {
      focused: this.focused,
      selection: [domElement.selectionStart, domElement.selectionEnd],
      value: inputElement.target.value
    };
  }

  /** Update Term info details into bundle */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
  public updateTermDetailsIntoBundle(): void {
    this.ProcessShareCertificate();
    this.l360Service.UpdateBundleIndex(this.allProducts).subscribe(() => {
      this.bundleHelperService.refreshBundle();
      // Form Validation
      this.ValidatePage();
      this.updateSelection();
    });
  }

  public ProcessShareCertificate(): void {
    this.shareCertificate.forEach((element) => {
      element.term.forEach((x) => {
        x.isSelected = element.isSelected;
        this.allProducts.filter((p) => p.id === x.id).forEach((p) => (p.isSelected = x.isSelected));
      });
    });
  }
  // Update isSelected property of Account/Optionsl Servies
  public UpdateBundleIndex(): void {
    this.ProcessShareCertificate();
    this.l360Service.UpdateBundleIndex(this.allProducts).subscribe(() => {
      this.bundleHelperService.refreshBundle();
    });
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public isErrorMessageAltDisplay(account: any, term: any): boolean {
    return account.isSelected && !(term.filter((x) => x.terms.some((a) => a.isSelected === true)).length > 0);
  }

  /** When user uncheck the account selection, clear all other services selection inside the respective parent service */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public resetChildSelectionAlt(account: any, shouldUpdateSelection = true): void {
    account.term.forEach((x, i) => {
      if (!account.isSelected && x.terms) {
        x.expanded = account.isSelected;
        x.terms.map((element) => {
          element.isSelected = false;
          element.shareNickName = '';
          element.termInMonths = '';
          element.dividendOption = '';
          element.maturityOption = '';
          element.taxYear = '';
          element.contributionSource = '';
          element.contributionAmount = '';
        });
        this.coAppProducts.forEach((app) => {
          app.shares[i].terms.forEach((element) => {
            element.isSelected = false;
            element.shareNickName = '';
            element.termInMonths = '';
            element.dividendOption = '';
            element.maturityOption = '';
            element.taxYear = '';
            element.contributionSource = '';
            element.contributionAmount = '';
          });
        });
      }
    });
    this.UpdateBundleIndex();
    if (shouldUpdateSelection) {
      this.updateSelection();
      this.ValidatePage();
    }
  }

  public isShareCertificate(product: Product): boolean {
    return product.type.toLowerCase() === 'sharecertificates';
  }

  // On Selection of Co Applicant's Share checkbox
  public coAppShareSelection(parentAccount: Product, coApplicantProduct: ApplicantProduct): void {
    const shareId = parentAccount.id;
    const coAppProduct = coApplicantProduct.shares.filter((f) => f.id === shareId)[0];
    const isSelected = coAppProduct.terms.some((t) => t.isSelected);
    const rawReqBody = [];

    // Set Co Applicant Share Selection Status
    rawReqBody.push(this.generateRequestBody(coApplicantProduct.bundlePath, coAppProduct.bundleIndex, 'isSelected', isSelected));
    rawReqBody.push(this.generateRequestBody(coApplicantProduct.bundlePath, coAppProduct.bundleIndex, 'terms/0/isSelected', isSelected));

    // Check whether max allowed applicant selection met or not, if so, then disable other shares
    const maxAllowedApplicant = parentAccount.numberOfJointApplicants;
    let selectedCoApp = 0;

    // Count Total Selected co Applicant Share
    parentAccount.coApplicants.forEach((applicant) => {
      applicant.shares
        .filter((f) => f.id === shareId)
        .forEach((item) => {
          item.isSelected = item.terms.some((t) => t.isSelected);
          selectedCoApp = item.isSelected ? selectedCoApp + 1 : selectedCoApp;
        });
    });

    // If Max Allowed Co App selection equals to selected co app shares then disable remaing co applicant
    if (selectedCoApp === maxAllowedApplicant) {
      parentAccount.coApplicants.forEach((applicant) => {
        applicant.shares
          .filter((f) => f.id === shareId)
          .forEach((share) => {
            share.isDisabled = share.isSelected ? false : true;
            rawReqBody.push(this.generateRequestBody(applicant.bundlePath, share.bundleIndex, 'isDisabled', share.isDisabled));
          });
      });
    } else {
      this.coAppProducts.forEach((applicant) => {
        applicant.shares
          .filter((f) => f.id === shareId)
          .forEach((share) => {
            share.isDisabled = this.isYouthMembership() ? true : false;
            rawReqBody.push(this.generateRequestBody(applicant.bundlePath, share.bundleIndex, 'isDisabled', share.isDisabled));
          });
      });
    }

    setTimeout(() => {
      firstValueFrom(this.applicationApiService.bundle(rawReqBody))
        .then(() => {})
        .catch(() => {});
    }, 0);
    this.updateSelection();
  }

  /** Generate Request Body */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public generateRequestBody(shareBundlePath: string, shareBundleIndex: number, propName: string, value: boolean): any {
    return {
      op: 'add',
      path: `oaoMemberInformation/accountsAndServices/${shareBundlePath}/shares/${shareBundleIndex}/${propName}`,
      value
    };
  }

  /** Check has coapplicants */
  public hasCoApplicant(): boolean {
    const membershipType = this.statefulService.wrap('api:oaoMemberInformation/membership/membershipTypeName').value;
    if (membershipType && membershipType.toString().toLowerCase() === 'business') {
      const responsibleParties = this.statefulService.arraySize('api:oaoMemberInformation/businessInformation/responsibleParties');
      return responsibleParties && responsibleParties > 1;
    }
    return this.statefulService.wrap('api:oaoMemberInformation/hasCoApplicant').value as boolean;
  }

  /** Check has optional Services */
  public hasCoApplicantShares(indexParent: number): boolean {
    // Check Is any co-applicant has the same share & its optional params
    const hasCoApplicantShares = [];
    this.coAppProducts.forEach((applicant) => {
      if (applicant.shares[indexParent]) {
        hasCoApplicantShares.push(true);
      }
    });
    // eslint-disable-next-line eqeqeq
    return hasCoApplicantShares.length > 0 && hasCoApplicantShares.filter((f) => f).length > 0;
  }

  public isYouthMembership(): boolean {
    const value = this.statefulService.wrap('api:oaoMemberInformation/membership/isYouth').valueString;
    return value ? JSON.parse(value) : false;
  }

  /** check Joint Applicant availability. If youth membership, no need to check this condition.*/
  public hasJointApplicants(product: Product): boolean {
    if (this.isYouthMembership()) {
      return true;
    } else {
      return product.numberOfJointApplicants && product.numberOfJointApplicants > 0;
    }
  }

  public initGridColumns() {
    // Define columns
    this.gridColumns = [
      {
        field: 'isSelected',
        title: 'Select a share',
        width: '35px',
        hideTitle: true,
        hidden: false,
        canToggle: true,
        template: this.selectionTemplate
      },
      {
        field: 'termFrom',
        title: 'Terms (mo)',
        width: '55px',
        canToggle: false,
        sortable: true,
        hidden: false,
        template: this.term
      },
      {
        field: 'apy',
        title: 'APY',
        width: '55px',
        hidden: false,
        canToggle: true,
        sortable: true
      },
      {
        field: 'rate',
        title: 'Dividend Rate',
        width: '65px',
        hidden: false,
        canToggle: false,
        sortable: true
      },
      {
        field: 'minBalance',
        title: 'Min. Balance',
        width: '65px',
        hidden: false,
        canToggle: false,
        sortable: true,
        template: this.minBalance
      }
    ];
  }

  public initGridOptions() {
    this.gridOptions = {
      initialSize: 50,
      globalSearch: false,
      globalColumnToggle: false,
      id: 'term-grid',
      rowsExpandable: true,
      rowsExpandableTemplate: this.gridForm,
      pagination: false,
      hideToolbar: true,
      rowCallback: (dataItem) => {
        /** Sync row expansion with row selection */
        dataItem.terms.forEach((t) => {
          t.isSelected = dataItem.expanded;
          this.termSelectionChange(dataItem, t);
        });
      }
    };
  }
}
